// src/pages/Instance/.../Billing/index.jsx
import React, { useEffect, useState, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import axios from "axios";
import { AuthContext } from "../../../../helpers/AuthContext";
import QboVendorLinkDialog from "../../../../components/QboVendorLinkDialog"; // ✅ link Supplier⇄Vendor
import { buildSourceExternalKey, normalizeAccountingSourceId, normalizeAccountingSourceType } from "../../../../utils/sourceExternalKey";

import {
  Box,
  Button,
  Chip,
  TextField,
  Typography,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Menu,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Skeleton,
  Stack,
  Divider,
  InputAdornment,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  UITableShell,
  UITableToolbar,
  UITableActionCell,
  UIStatusChip,
} from "../../../../components/ui";
import {
  canOpenBillFromInvoiceNumber,
  getBillInvoiceNumberDisplay,
} from "../../../../utils/billInternalDecoration";
import { BILL_TYPES, filterBillsByParty, getOtherServiceProviders } from "../../../../utils/billParty";

// Views (same idea as invoicing)
const VIEW = { BILLS: "bills", EXPORTED: "exported" };
const QB_ICON =
  "https://cdn-1.webcatalog.io/catalog/quickbooks/quickbooks-icon-filled-256.webp?v=1757897011406";

// small helper
const fmtMoney = (n, cur = "") => {
  const v = Number(n || 0);
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)} ${cur || ""}`.trim();
};

const parseMaybeJson = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try { return JSON.parse(value); } catch { return null; }
  }
  return null;
};

const getBillingSupplierName = (row) => {
  const billSupplier = parseMaybeJson(row?.billSupplier);
  const supplierInfo = parseMaybeJson(row?.supplierInfo || row?.billSupplierInfo);
  const items = Array.isArray(row?.items) ? row.items : parseMaybeJson(row?.items);
  const firstItem = Array.isArray(items) ? items[0] : null;

  return (
    billSupplier?.supplierName ||
    billSupplier?.name ||
    billSupplier?.Name ||
    supplierInfo?.supplierName ||
    supplierInfo?.name ||
    supplierInfo?.Name ||
    firstItem?.supplier?.Name ||
    firstItem?.supplier?.name ||
    firstItem?.SupplierName ||
    firstItem?.supplierName ||
    (typeof row?.billSupplier === "string" && !row.billSupplier.trim().startsWith("{") ? row.billSupplier : "") ||
    row?.supplierName ||
    "—"
  );
};

const isBillExported = (b) => {
  const qboStatus = String(b?.qboExportStatus || "").trim().toLowerCase();
  const genericStatus = String(b?.exportStatus || "").trim().toLowerCase();
  const exportMethod = String(b?.exportMethod || "").trim().toLowerCase();

  if (qboStatus === "exported") return true;

  return (
    genericStatus === "exported" ||
    !!b?.qboBillId ||
    !!b?.exported_at ||
    !!b?.exportedAt ||
    !!b?.exportBatchId ||
    ["csv", "xlsx", "qbo"].includes(exportMethod)
  );
};

export default function InstanceBilling() {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);

  const theme = useTheme();
  const C = {
    paperBg: theme.palette.background.paper,
    subtleBg: theme.palette.action.hover,
    border: theme.palette.divider,
    textPri: theme.palette.text.primary,
    textSec: theme.palette.text.secondary,
    textMuted: theme.palette.text.disabled,
    white: theme.palette.common.white,
    headerBg: theme.eliteCore?.tokens?.color?.neutral?.[800] ?? "#1e293b",
    headerBgHover: theme.eliteCore?.tokens?.color?.neutral?.[700] ?? "#334155",
  };

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = {
    headers: { accessToken: localStorage.getItem("accessToken") },
  };

  const [activeTab, setActiveTab] = useState("search");
  const [view, setView] = useState(VIEW.BILLS);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [allRows, setRows] = useState([]);
  const [billType, setBillType] = useState(BILL_TYPES.ALL);
  const [serviceProvider, setServiceProvider] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [exportingId, setExportingId] = useState(null);
  const [fileExportMenuEl, setFileExportMenuEl] = useState(null);
  const [reportMenuEl, setReportMenuEl] = useState(null);

  // QBO status (reuse invoices status endpoint)
  const [qbo, setQbo] = useState({
    connected: false,
    realmId: null,
    checking: false,
  });
  const [qboExportEnabled, setQboExportEnabled] = useState(false);

  // 🔗 Link-to-QBO Vendor dialog state
  const [qboLinkOpen, setQboLinkOpen] = useState(false);
  const [qboLinkCtx, setQboLinkCtx] = useState({
    partyType: "supplier",
    partyId: null,
    partyName: "",
    supplierId: null,
    supplierName: "",
    supplierSource: null,
    selectedSupplierOption: null,
    decoratorId: null,
    decoratorName: "",
    billId: null, // ← real Billing.id
    sourceType: null,
    sourceId: null,
    accountingSourceId: null,
    sourceExternalKey: null,
  });

  // ➕ Create-QBO Customer dialog state
  const [qboCustomerOpen, setQboCustomerOpen] = useState(false);

  // Column menu state
  const [visibleCols, setVisibleCols] = useState([]);
  const [colMenuEl, setColMenuEl] = useState(null);
  const openColMenu = Boolean(colMenuEl);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  const [density, setDensity] = useState("comfortable");
  const [densityMenuEl, setDensityMenuEl] = useState(null);

  const serviceProviders = useMemo(() => getOtherServiceProviders(allRows), [allRows]);
  const rows = useMemo(() => filterBillsByParty(allRows, billType, serviceProvider),
    [allRows, billType, serviceProvider]);
  const summary = useMemo(() => ({
    count: rows.length,
    total: rows.reduce((sum, row) => sum + Number(row.poAmount || row.amount || 0), 0),
  }), [rows]);

  const densitySettings = {
    compact: { py: 0.75, fontSize: "0.8125rem" },
    comfortable: { py: 1.5, fontSize: "0.875rem" },
    spacious: { py: 2.25, fontSize: "0.9375rem" },
  };

  // Local look & feel (mirrors Invoicing)
  const UI = {
    color: {
      black: "#000",
      charcoal: "#444",
      darkRed: "#a30000",
      qbGreen: "#2ca01c",
      qbGreenHover: "#279018",
      blue: "#5757cb",
      blueHover: "#1669c1",
      teal: "#00897b",
      tealHover: "#00766a",
      amber: "#f39c12",
      amberHover: "#d98200",
      outlineHoverBg: "#f7f7f7",
    },
  };

  const getBillHref = (row) => {
    if (!canOpenBillFromInvoiceNumber(row)) return "";
    const base = process.env.FRONTEND_APP_URL || window.location.origin;
    return `${base}/instance/${instanceUniqueId}/sales/orders/${row.orderId}/bills/${row.billId}`;
  };

  const sxBtnBase = {
    textTransform: "capitalize",
    fontFamily: '"Nunito", sans-serif',
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.2,
  };
  const sxGetReport = {
    ...sxBtnBase,
    backgroundColor: UI.color.blue,
    "&:hover": { backgroundColor: UI.color.blueHover },
  };
  const sxExportSelected = (enabled) =>
    enabled
      ? {
          ...sxBtnBase,
          backgroundColor: UI.color.qbGreen,
          "&:hover": { backgroundColor: UI.color.qbGreenHover },
        }
      : sxBtnBase;
  const sxDownloadCsv = {
    ...sxBtnBase,
    backgroundColor: UI.color.teal,
    "&:hover": { backgroundColor: UI.color.tealHover },
  };
  const sxRefresh = {
    ...sxBtnBase,
    color: UI.color.charcoal,
    borderColor: UI.color.charcoal,
    "&:hover": {
      color: UI.color.black,
      borderColor: UI.color.black,
      background: UI.color.outlineHoverBg,
    },
  };
  const sxClear = {
    ...sxBtnBase,
    color: UI.color.charcoal,
    borderColor: UI.color.charcoal,
    "&:hover": {
      color: UI.color.black,
      borderColor: UI.color.black,
      background: UI.color.outlineHoverBg,
    },
  };
  const sxDateField = {
    "& .MuiInputBase-root": { backgroundColor: "#FFF" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#c2c2c2" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: UI.color.blue,
    },
  };

  // ─────────────────────────────────────────────
  // Load QBO status (reuse invoices route)
  // ─────────────────────────────────────────────
  const fetchQboStatus = async () => {
    setQbo((q) => ({ ...q, checking: true }));
    try {
      const res = await axios.get(`${SERVER}/invoices/qbo/status`, {
        ...headers,
        params: { instanceUniqueId },
      });
      setQbo({
        connected: !!res.data.connected,
        realmId: res.data.realmId || null,
        checking: false,
      });
    } catch (e) {
      setQbo({ connected: false, realmId: null, checking: false });
      console.error("QBO status error", e);
    }
  };

  const fetchQboSettingsFlag = async () => {
    try {
      const res = await axios.get(`${SERVER}/invoices/qbo/settings`, {
        ...headers,
        params: { instanceUniqueId },
      });
      setQboExportEnabled(!!res?.data?.qbo_export_enabled);
    } catch (e) {
      console.error("QBO settings load error", e?.response?.data || e);
      setQboExportEnabled(false);
    }
  };

  const handleQboConnect = async () => {
    try {
      const res = await axios.get(`${SERVER}/invoices/qbo/connect`, {
        ...headers,
        params: { instanceUniqueId },
      });
      const url = res.data?.url;
      if (!url) return alert(`Server did not return OAuth URL.`);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("QBO connect error", e?.response?.data || e);
      alert(
        `Could not start QuickBooks connection.\n${
          e?.response?.data?.error || e.message
        }`,
      );
    }
  };

  const handleSort = (colId) => {
    if (sortBy === colId) {
      if (sortDir === "asc") setSortDir("desc");
      else {
        setSortBy("");
        setSortDir("asc");
      }
    } else {
      setSortBy(colId);
      setSortDir("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortBy) return rows;
    return [...rows].sort((a, b) => {
      if (sortBy === "poAmount" || sortBy === "billTotal") {
        const av = Number(a[sortBy] || 0),
          bv = Number(b[sortBy] || 0);
        return sortDir === "asc" ? av - bv : bv - av;
      }
      const fieldMap = {
        invoiceDate: "invoiceDate",
        exported_at: "exported_at",
        createdAt: "createdAt",
        poNumber: "poNumber",
        orderId: "orderId",
      };
      const av = String(a[fieldMap[sortBy]] || "");
      const bv = String(b[fieldMap[sortBy]] || "");
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [rows, sortBy, sortDir]);

  const pagedRows = useMemo(
    () =>
      sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage],
  );

  // ─────────────────────────────────────────────
  // Fetch Orders (all pages) then Bills per order
  // ─────────────────────────────────────────────
  async function fetchAllOrders({ instanceUniqueId, baseRpp = 200 }) {
    let page = 1;
    const all = [];
    for (;;) {
      const res = await axios.get(`${SERVER}/orders/get-orders`, {
        ...headers,
        params: { instanceUniqueId, page, rpp: baseRpp },
      });
      const r = res?.data?.Results || [];
      const totalPages = res?.data?.TotalPages || 1;
      all.push(...r);
      if (page >= totalPages || r.length === 0) break;
      page += 1;
    }
    return all;
  }

  const fetchReport = async (overrides = {}) => {
    const effectiveView = overrides.view ?? view;
    setLoading(true);
    try {
      // 1) get orders (all pages) for PO filter and enrich (poNumber)
      let orders = await fetchAllOrders({ instanceUniqueId, baseRpp: 200 });

      // 2) filter by PO (optional)
      if (poNumber) {
        orders = orders.filter((o) => String(o.OrderId) === String(poNumber));
      }

      const orderById = new Map(orders.map((o) => [String(o.OrderId), o]));

      // helper: normalize a Bill row only
      const normalizeBill = (row, orderId) => {
        const o = orderById.get(String(orderId));
        const poNum = row.poNumber ?? row.customerPO ?? o?.OrderId ?? "";
        const billId = row.id ?? row.ID ?? row.billId ?? null;
        const billTotal = Number(
          row.amount ??
            row.amountDue ??
            (Number(row.subTotal || 0) + Number(row.totalTax || 0)) ??
            0,
        );
      
        if (!billId) return null; // only keep real bills

        return {
          // UI identity
          id: `bill_${billId}`,

          // server identities
          billId,
          source: "bill",

          // display
          orderId: row.orderId ?? orderId,
          poNumber: poNum,
          billSupplier: row.billSupplier ?? row.vendor ?? row.supplier ?? null,
          billSupplierInfo: row.billSupplierInfo ?? null,
          supplierInfo: row.supplierInfo ?? row.billSupplierInfo ?? null,
          items: row.items ?? null,

          billPartyName: row.billPartyName ?? null,
          billPartyType: row.billPartyType ?? (row.decoratorId != null ? "decorator" : "supplier"),
          billDecoratorInfo: row.billDecoratorInfo ?? null,
          decoratorId: row.decoratorId ?? row.DecoratorId ?? null,
          isInternalDecoration: row.isInternalDecoration === true,
          sourceType: row.sourceType ?? parseMaybeJson(row.billSupplier)?.sourceType ?? null,
          sourceId: row.sourceId ?? parseMaybeJson(row.billSupplier)?.sourceId ?? null,

          // money/date/status (from server)
          poAmount: row.poAmount ?? 0,
          invoice: row.invoice ?? null,
          billTotal,
          amount: row.amount ?? null,
          amountDue: row.amountDue ?? null,
          subTotal: row.subTotal ?? null,
          totalTax: row.totalTax ?? null,
          currency: row.currency,
          invoiceDate: row.invoiceDate ?? row.date ?? null,

          qboExportStatus: row.qboExportStatus ?? row.exportStatus ?? "",
          qboDocNumber: row.qboDocNumber ?? null,
          qboBillId: row.qboBillId ?? row.qbo_id ?? null,
          qboLastSyncAt: row.qboLastSyncAt ?? null,
          exported_at: row.exported_at ?? null,
          qboSyncError: row.qboSyncError ?? null,
          exportStatus: row.exportStatus ?? null,
          exportedAt: row.exportedAt ?? null,
          exportedById: row.exportedById ?? null,
          exportMethod: row.exportMethod ?? null,
          exportBatchId: row.exportBatchId ?? null,
          exportDestination: row.exportDestination ?? null,
          exportFileName: row.exportFileName ?? null,
          exportError: row.exportError ?? null,

          createdAt: row.createdAt ?? null,
          updatedAt: row.updatedAt ?? null,
          allBillsReceived: row.allBillsReceived ?? false,

          supplierId:
            row.supplierId ??
            row.SupplierId ??
            parseMaybeJson(row.billSupplier)?.id ??
            parseMaybeJson(row.supplierInfo)?.id ??
            null,
          supplierSource:
            row.supplierSource ??
            row.SupplierSource ??
            row.accountingSupplierSource ??
            parseMaybeJson(row.billSupplier)?.supplierSource ??
            parseMaybeJson(row.supplierInfo)?.supplierSource ??
            null,
        };
      };

    // 3) load Bills per order (no more invoice fallback)
    const nested = await Promise.all(
      orders.map(async (o) => {
        const oid = o.OrderId;
        try {
          const r1 = await axios.get(`${SERVER}/bill/orders/${oid}`, {
            ...headers,
            params: { instanceUniqueId },
          });
          const a = r1?.data?.billingData || r1?.data?.data || []; // tolerate either key
          if (!Array.isArray(a) || a.length === 0) return [];
          return a.map((b) => normalizeBill(b, oid)).filter(Boolean);
        } catch (e) {
          console.error(`Failed to load bills for order ${oid}`, e?.response?.data || e);
          return [];
        }
      })
    );

      // 4) flatten + dedupe by id
      const seen = new Set();
      let list = nested.flat().filter((b) => {
        if (!b || !b.id) return false;
        if (seen.has(b.id)) return false;
        seen.add(b.id);
        return true;
      });

      // 5) view filters (exported vs bills)
      if (effectiveView === VIEW.EXPORTED) {
        list = list.filter(isBillExported);
      } else if (effectiveView === VIEW.BILLS) {
        list = list.filter((b) => !isBillExported(b));
      }

      // 6) date filters (Bill date vs Export date)
      if (dateFrom || dateTo) {
        const fromTs = dateFrom ? new Date(dateFrom).getTime() : null;
        const toTs = dateTo
          ? new Date(dateTo).getTime() + 24 * 3600 * 1000 - 1
          : null; // inclusive
        list = list.filter((b) => {
          const basis =
            isBillExported(b)
              ? b.exported_at || b.qboLastSyncAt || b.updatedAt || b.createdAt
              : b.invoiceDate || b.updatedAt || b.createdAt;
          if (!basis) return true;
          const ts = new Date(basis).getTime();
          if (fromTs && ts < fromTs) return false;
          if (toTs && ts > toTs) return false;
          return true;
        });
      }

      setRows(list);

      console.debug(`Billing fetch: view=${effectiveView}, orders=${orders.length}, rows=${list.length}`);
    } catch (e) {
      console.error("Billing report error:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const removeRowsById = (idsToRemove = []) => {
    const removeSet = new Set();
    idsToRemove.forEach((id) => {
      const key = String(id);
      removeSet.add(key);
      if (key.startsWith("bill_")) removeSet.add(key.replace("bill_", ""));
      else removeSet.add(`bill_${key}`);
    });

    setRows((prev) => {
      const next = prev.filter((r) => {
        const uiId = String(r?.id ?? "");
        const billId = String(r?.billId ?? "");
        return !removeSet.has(uiId) && !removeSet.has(billId);
      });
      return next;
    });

    setSelected((prev) =>
      prev.filter((id) => {
        const key = String(id);
        const alt = key.startsWith("bill_") ? key.replace("bill_", "") : `bill_${key}`;
        return !removeSet.has(key) && !removeSet.has(alt);
      }),
    );
  };

  useEffect(() => {
    if (!instanceUniqueId) return;
    fetchQboStatus();
    fetchQboSettingsFlag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (!instanceUniqueId) return;
    setSelected([]);
    setPage(0);
    fetchReport({ view });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId, view]);

  // ─────────────────────────────────────────────
  // Vendor Link dialog helpers
  // ─────────────────────────────────────────────
  const openLinkForBill = (row) => {
    const supplierName = getBillingSupplierName(row);
    const billSupplierObj = parseMaybeJson(row?.billSupplier);
    const supplierInfoObj = parseMaybeJson(row?.supplierInfo || row?.billSupplierInfo);
    const rowPartyType = String(row?.billPartyType || billSupplierObj?.type || "").trim().toLowerCase();
    const shippingAccountId =
      rowPartyType === "shipping-account"
        ? (
            row.shippingAccountId ||
            row.billSupplierInfo?.shippingAccountId ||
            row.billSupplierInfo?.id ||
            billSupplierObj?.shippingAccountId ||
            billSupplierObj?.id ||
            null
          )
        : null;
    const resolvedPartyType = rowPartyType === "other-service"
      ? "other-service"
      : rowPartyType === "shipping-account" ? "shipping-account" : (rowPartyType === "decorator" ? "decorator" : "supplier");
    const accountingSourceType = normalizeAccountingSourceType(row.sourceType || billSupplierObj?.sourceType || null);
    const accountingSourceId = normalizeAccountingSourceId(
      row.sourceId || billSupplierObj?.sourceId || billSupplierObj?.sourceKey || billSupplierObj?.courier || null,
    );
    const sourceExternalKey = resolvedPartyType === "other-service" || accountingSourceType === "courier"
      ? buildSourceExternalKey(accountingSourceType, accountingSourceId) || null
      : null;
    const partyId = resolvedPartyType === "shipping-account"
      ? shippingAccountId
      : resolvedPartyType === "decorator"
        ? row.decoratorId || null
        : resolvedPartyType === "other-service"
          ? (/^\d+$/.test(String(accountingSourceId || "")) ? Number(accountingSourceId) : null)
          : row.supplierId || billSupplierObj?.id || supplierInfoObj?.id || null;
    const partyName = supplierName === "—" ? "" : supplierName;
    const selectedSupplierOption = supplierInfoObj?.id || supplierInfoObj?.Id || billSupplierObj?.id || billSupplierObj?.Id
      ? {
          ...(supplierInfoObj || billSupplierObj || {}),
          id: supplierInfoObj?.id || supplierInfoObj?.Id || billSupplierObj?.id || billSupplierObj?.Id,
          Id: supplierInfoObj?.Id || supplierInfoObj?.id || billSupplierObj?.Id || billSupplierObj?.id,
          Name: supplierInfoObj?.Name || supplierInfoObj?.name || billSupplierObj?.Name || billSupplierObj?.name || supplierName || "",
          supplierSource: row.supplierSource || supplierInfoObj?.supplierSource || billSupplierObj?.supplierSource || null,
          SupplierSource: row.supplierSource || supplierInfoObj?.SupplierSource || billSupplierObj?.SupplierSource || null,
        }
      : null;

    setQboLinkCtx({
      partyType: resolvedPartyType,
      partyId,
      partyName,
      supplierId:
        row.supplierId ||
        billSupplierObj?.id ||
        supplierInfoObj?.id ||
        null,
      supplierName: partyName,
      supplierSource: row.supplierSource || supplierInfoObj?.supplierSource || billSupplierObj?.supplierSource || null,
      selectedSupplierOption,
      decoratorId: row.decoratorId || null,
      decoratorName: row.billDecoratorInfo?.title || row.billDecoratorInfo?.name || row.billPartyName || null,
      billId: row.billId || null, // ← use the real Billing.id if present
      sourceType: accountingSourceType,
      sourceId: accountingSourceId,
      accountingSourceId: resolvedPartyType === "other-service" ? partyId : null,
      sourceExternalKey,
    });
    setQboLinkOpen(true);
  };

  // ─────────────────────────────────────────────
  // QBO export calls
  // ─────────────────────────────────────────────
  const exportOne = async (row, { skipSpinner = false } = {}) => {
    if (!qboExportEnabled)
      return alert("Enable QBO Exports in Integrations first.");
    if (!qbo.connected) return alert("Connect QuickBooks first.");

    // Only Bills are exportable here. Invoice rows are from a fallback.
    if (!row.billId) {
      return alert(
        "This row wasn’t returned by the Bills API, so it doesn’t have a Bill ID yet.\n" +
          "Open the Sales Order → Bills and create/link the Bill, or ensure /bill/orders/:id returns it."
      );
    }

    try {
      if (!skipSpinner) setExportingId(row.id);
      console.debug("QBO export start", {
        uiId: row.id,
        billId: row.billId,
        orderId: row.orderId,
        source: row.source,
      });

      // ✅ send the real Billing.id
      const res = await axios.post(
        `${SERVER}/bill/${row.billId}/export/qbo`,
        { instanceUniqueId },
        headers,
      );
      const { id, docNumber } = res.data || {};

      const nowIso = new Date().toISOString();
      if (view === VIEW.BILLS) {
        removeRowsById([row.id, row.billId]);
      } else {
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  qboExportStatus: "exported",
                  qboBillId: id,
                  qboDocNumber: docNumber,
                  qboLastSyncAt: nowIso,
                  exported_at: nowIso,
                  qboSyncError: null,
                }
              : r,
          ),
        );
        setSelected((prev) => prev.filter((sid) => String(sid) !== String(row.id)));
      }
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        "Export failed. Ensure supplier is linked to a QBO Vendor.";
      alert(msg);
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                qboExportStatus: "failed",
                qboSyncError: msg,
                qboLastSyncAt: new Date().toISOString(),
              }
            : r,
        ),
      );
      if (/VENDOR_UNMAPPED|NOT\s+LINKED|QUICKBOOKS\s+VENDOR|SUPPLIER(?:\s+IS)?\s+NOT\s*LINKED/i.test(msg)) {
        openLinkForBill(row);
      }
    } finally {
      if (!skipSpinner) setExportingId(null);
    }
  };

  const exportSelected = async () => {
    if (!qboExportEnabled)
      return alert("Enable QBO Exports in Integrations first.");
    if (!qbo.connected) return alert("Connect QuickBooks first.");
    const confirmed = window.confirm("This will send selected records to QuickBooks and mark them Exported.");
    if (!confirmed) return;
    const map = new Map(rows.map((r) => [r.id, r]));
    const toExport = selected
      .map((id) => map.get(id))
      .filter((r) => r && r.billId && r.qboExportStatus !== "exported"); // ← only real Bills
    const attempted = [];
    for (const r of toExport) {
      attempted.push(r.id, r.billId);
      // eslint-disable-next-line no-await-in-loop
      await exportOne(r, { skipSpinner: true });
    }
    removeRowsById(attempted);
    await fetchReport({ view });
  };

  const selectedBillIds = () => {
    const map = new Map(rows.map((r) => [String(r.id), r]));
    return selected
      .map((uiId) => map.get(String(uiId)))
      .map((row) => row?.billId)
      .filter(Boolean);
  };

  const downloadFromResponse = async (res, fallbackName) => {
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const runBillingFileExport = async (method) => {
    const ids = selectedBillIds();
    if (!ids.length) return alert("Select at least one bill.");
    const confirmed = window.confirm("This will generate a file and mark selected records as Exported.");
    if (!confirmed) return;
    try {
      const res = await fetch(`${SERVER}/bill/export/${method}`, {
        method: "POST",
        headers: {
          accessToken: localStorage.getItem("accessToken"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ instanceUniqueId, ids, markExported: true }),
      });
      if (!res.ok) return alert(`Export failed: ${await res.text()}`);
      await downloadFromResponse(
        res,
        `bills-export-${new Date().toISOString().slice(0, 10)}.${method}`,
      );
      await fetchReport({ view });
      setSelected([]);
      alert("File export completed.");
    } catch (e) {
      alert(e?.message || "File export failed.");
    }
  };

  const runBillingReportDownload = async (method) => {
    try {
      const params = new URLSearchParams({ instanceUniqueId });
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);
      if (billType === BILL_TYPES.INTERNAL_DECORATION) params.append("internalDecorationFilter", "internal_only");
      params.append("ids", rows.map((row) => row.billId).filter(Boolean).join(","));
      const res = await fetch(`${SERVER}/bill/report/${method}?${params}`, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      if (!res.ok) return alert(`Download failed: ${await res.text()}`);
      await downloadFromResponse(
        res,
        `bills-report-${new Date().toISOString().slice(0, 10)}.${method}`,
      );
    } catch (e) {
      alert(e?.message || "Report download failed.");
    }
  };

  const bulkUnexportBillingSelected = async () => {
    if (view !== VIEW.EXPORTED) return;
    const ids = selectedBillIds();
    if (!ids.length) return alert("Select at least one exported bill.");
    const ok = window.confirm(
      "This resets export status in Elite Core only. It does not remove or reverse records in QuickBooks or external accounting systems.",
    );
    if (!ok) return;
    const reason = window.prompt("Reason for unexport:", "");
    if (!String(reason || "").trim()) return alert("Reason is required.");
    try {
      const res = await axios.post(
        `${SERVER}/bill/unexport`,
        { ids, reason: String(reason).trim() },
        headers,
      );
      alert(`Unexported ${res?.data?.count || 0} bills.`);
      await fetchReport({ view });
      setSelected([]);
    } catch (e) {
      alert(e?.response?.data?.error || "Unexport failed.");
    }
  };

  // ─────────────────────────────────────────────
  // Table columns (toggleable + actions)
  // ─────────────────────────────────────────────
  const statusChip = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "exported")
      return <Chip size="small" label="Exported" color="success" />;
    if (s === "failed")
      return <Chip size="small" label="Failed" color="error" />;
    return <Chip size="small" label="Pending" />;
  };

  const actionColBills = {
    field: "actions",
    headerName: "Actions",
    width: 420,
    sortable: false,
    renderCell: ({ row }) => {
      const alreadyExported = row.qboExportStatus === "exported";
      const disabled =
        !qboExportEnabled || !qbo.connected || alreadyExported || !row.billId; // ← disable if no Bill id
      const label = !qboExportEnabled
        ? "QBO exports disabled"
        : !qbo.connected
        ? "Connect QBO"
        : alreadyExported
        ? "Exported"
        : "Export to QBO";
      const connectedGreen =
        qboExportEnabled && qbo.connected && !alreadyExported && !!row.billId;
      const showLinkBtn =
        row.qboSyncError &&
        /VENDOR\s*UNMAPPED|SUPPLIER(?:\s+IS)?\s+NOT\s*LINKED|QUICKBOOKS\s+VENDOR|VendorRef\s*missing/i.test(
          String(row.qboSyncError),
        );

      return (
        <Box
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
          className="table-row-actions"
        >
          <Button
            size="small"
            variant="contained"
            disabled={disabled}
            onClick={() => exportOne(row)}
            startIcon={
              <img src={QB_ICON} alt="" style={{ width: 16, height: 16 }} />
            }
            sx={{
              ...sxBtnBase,
              ...(connectedGreen
                ? {
                    backgroundColor: "#2ca01c",
                    "&:hover": { backgroundColor: "#279018" },
                  }
                : {}),
            }}
          >
            {exportingId === row.id ? <CircularProgress size={18} /> : label}
          </Button>

          {/* quick link CTA if unmapped vendor */}
          {showLinkBtn && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => openLinkForBill(row)}
              sx={sxBtnBase}
            >
              Link QBO Vendor
            </Button>
          )}

          {row.qboSyncError && (
            <Tooltip title={row.qboSyncError}>
              <Chip size="small" label="Error" color="error" />
            </Tooltip>
          )}
        </Box>
      );
    },
  };

  // Toggle registry
  const COLUMN_DEFS = {
    id: {
      label: "ID",
      build: () => ({ field: "id", headerName: "ID", width: 80 }),
    },
    poNumber: {
      label: "PO #",
      build: () => ({
        field: "poNumber",
        headerName: "PO #",
        width: 140,
        renderCell: ({ row }) => {
          const base = process.env.FRONTEND_APP_URL || window.location.origin;
          const url = `${base}/instance/${instanceUniqueId}/sales/orders/${row.orderId}/purchase-order-home`;
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", color: "#1976d2" }}
            >
              {row.poNumber}
            </a>
          );
        },
      }),
    },
    orderId: {
      label: "Sales Order",
      build: () => ({
        field: "orderId",
        headerName: "Sales Order",
        width: 140,
      }),
    },
    supplier: {
      label: "Supplier",
      build: () => ({
        field: "billSupplier",
        headerName: "Supplier",
        width: 220,
        renderCell: ({ row }) => {
          const name =
            row.billSupplierInfo?.Name ??
            row.billSupplier?.name ??
            (typeof row.billSupplier === "string" ? row.billSupplier : "N/A");
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>{name || "N/A"}</span>
              <Tooltip title="Manage QBO Vendor link">
                <span>
                  <IconButton
                    size="small"
                    aria-label="manage vendor link"
                    onClick={() => openLinkForBill(row)}
                    title="Link Supplier to QBO Vendor"
                  >
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      }),
    },
    allBillsReceived: {
      label: "All Received?",
      build: () => ({
        field: "allBillsReceived",
        headerName: "All Received?",
        width: 130,
        valueGetter: (value, row) => (row?.allBillsReceived ? "Yes" : "No"),
      }),
    },
    poAmount: {
      label: "PO Amount",
      build: () => ({
        field: "poAmount",
        headerName: "PO Amount",
        width: 140,
        valueGetter: (value, row) => fmtMoney(row?.poAmount, row?.currency),
      }),
    },
    invoice: {
      label: "Invoice #",
      build: () => ({
        field: "invoice",
        headerName: "Invoice #",
        width: 140,
        valueGetter: (value, row) => getBillInvoiceNumberDisplay(row).label,
        renderCell: ({ row }) => {
          const display = getBillInvoiceNumberDisplay(row);
          const content = (
            <Typography
              variant="body2"
              sx={{
                color: display.missing ? "error.main" : display.notApplicable ? "text.secondary" : C.textPri,
                fontFamily: "monospace",
              }}
            >
              {display.label}
            </Typography>
          );
          if (canOpenBillFromInvoiceNumber(row)) {
            return (
              <a
                href={getBillHref(row)}
                style={{ textDecoration: "none" }}
                aria-label={`Open bill ${row.billId} for invoice ${display.label}`}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    fontFamily: "monospace",
                    fontWeight: 700,
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                  }}
                >
                  {display.label}
                </Typography>
              </a>
            );
          }
          return display.notApplicable
            ? <Tooltip title="Invoice number not required for internal decoration">{content}</Tooltip>
            : content;
        },
      }),
    },
    billTotal: {
      label: "Bill total",
      build: () => ({
        field: "billTotal",
        headerName: "Bill total",
        width: 140,
        valueGetter: (value, row) => fmtMoney(row?.billTotal, row?.currency),
      }),
    },
    invoiceDate: {
      // 🔄 UI copy aligned to "Bill"
      label: "Bill Date",
      build: () => ({
        field: "invoiceDate",
        headerName: "Bill Date",
        width: 160,
        valueGetter: (value, row) =>
          row?.invoiceDate
            ? new Date(row.invoiceDate).toLocaleDateString()
            : "—",
      }),
    },
    qboExportStatus: {
      label: "QBO Export",
      build: () => ({
        field: "qboExportStatus",
        headerName: "QBO Export",
        width: 140,
        renderCell: ({ row }) => statusChip(row.qboExportStatus),
      }),
    },
    qboDocNumber: {
      label: "QBO Doc #",
      build: () => ({
        field: "qboDocNumber",
        headerName: "QBO Doc #",
        width: 140,
        valueGetter: (value, row) => row?.qboDocNumber || "—",
      }),
    },
    exported_at: {
      label: "Exported At",
      build: () => ({
        field: "exported_at",
        headerName: "Exported At",
        width: 180,
        valueGetter: (value, row) =>
          row?.exported_at || row?.qboLastSyncAt
            ? new Date(row?.exported_at || row?.qboLastSyncAt).toLocaleString()
            : "—",
      }),
    },
    exportMethod: {
      label: "Export Method",
      build: () => ({
        field: "exportMethod",
        headerName: "Export Method",
        width: 140,
        valueGetter: (value, row) => row?.exportMethod || "—",
      }),
    },
    exportBatchId: {
      label: "Export Batch",
      build: () => ({
        field: "exportBatchId",
        headerName: "Export Batch",
        width: 220,
        valueGetter: (value, row) => row?.exportBatchId || "—",
      }),
    },
    exportedById: {
      label: "Exported By",
      build: () => ({
        field: "exportedById",
        headerName: "Exported By",
        width: 120,
        valueGetter: (value, row) => row?.exportedById || "—",
      }),
    },
    createdAt: {
      label: "Created",
      build: () => ({
        field: "createdAt",
        headerName: "Created",
        width: 180,
        valueGetter: (value, row) =>
          row?.createdAt ? new Date(row.createdAt).toLocaleString() : "—",
      }),
    },
  };

  // const DEFAULT_VISIBLE = {
  //   [VIEW.BILLS]: [
  //     "id",
  //     "poNumber",
  //     "orderId",
  //     "supplier",
  //     "allBillsReceived",
  //     "poAmount",
  //     "invoiceDate", // shows as "Bill Date"
  //     "qboExportStatus",
  //     "qboDocNumber",
  //     "createdAt",
  //   ],
  //   [VIEW.EXPORTED]: ["id", "poNumber", "orderId", "supplier", "poAmount", "qboExportStatus", "qboDocNumber", "exported_at"],
  // };

  const DEFAULT_VISIBLE = {
    [VIEW.BILLS]: [
      "id",
      "poNumber",
      "orderId",
      "supplier",
      "allBillsReceived",
      "poAmount",
      "invoice",
      "billTotal",
      "invoiceDate",
      "qboExportStatus",
      "qboDocNumber",
      "createdAt",
      "actions",
    ],
    [VIEW.EXPORTED]: [
      "id",
      "poNumber",
      "orderId",
      "supplier",
      "poAmount",
      "invoice",
      "billTotal",
      "qboExportStatus",
      "qboDocNumber",
      "exported_at",
      "exportMethod",
      "exportBatchId",
      "exportedById",
      "actions",
    ],
  };

  const columnsStorageKey = (id, v) => `billing.columns.${id}.${v}`;
  useEffect(() => {
    const key = columnsStorageKey(instanceUniqueId, view);
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr) && arr.length) {
          setVisibleCols(arr);
          return;
        }
      } catch {}
    }
    setVisibleCols(DEFAULT_VISIBLE[view]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId, view]);

  useEffect(() => {
    const key = columnsStorageKey(instanceUniqueId, view);
    try {
      localStorage.setItem(key, JSON.stringify(visibleCols));
    } catch {}
  }, [visibleCols, instanceUniqueId, view]);

  const toggleCol = (key) =>
    setVisibleCols((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  // const selectAllCols = () => setVisibleCols(Object.keys(COLUMN_DEFS));
  // const clearAllCols = () => setVisibleCols([]);

  const selectAllCols = () =>
    setVisibleCols(ALL_BILLING_COLUMNS.map((c) => c.id));
  const clearAllCols = () =>
    setVisibleCols(
      ALL_BILLING_COLUMNS.filter((c) => c.locked).map((c) => c.id),
    );

  const columns = useMemo(() => {
    const built = (visibleCols || [])
      .map((key) => COLUMN_DEFS[key]?.build())
      .filter(Boolean);
    if (view === VIEW.BILLS) return [...built, actionColBills];
    return built;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCols, view, qbo.connected, exportingId, rows]);

  const ALL_BILLING_COLUMNS = [
    { id: "id", label: "ID", sortable: false, minWidth: 80 },
    { id: "poNumber", label: "PO #", sortable: true, minWidth: 130 },
    { id: "orderId", label: "Sales Order", sortable: true, minWidth: 130 },
    { id: "supplier", label: "Supplier", sortable: false, minWidth: 220 },
    {
      id: "allBillsReceived",
      label: "All Received?",
      sortable: false,
      minWidth: 130,
    },
    { id: "poAmount", label: "PO Amount", sortable: true, minWidth: 140 },
    { id: "invoice", label: "Invoice #", sortable: true, minWidth: 140 },
    { id: "billTotal", label: "Bill total", sortable: true, minWidth: 140 },
    { id: "invoiceDate", label: "Bill Date", sortable: true, minWidth: 160 },
    {
      id: "qboExportStatus",
      label: "QBO Export",
      sortable: false,
      minWidth: 140,
    },
    { id: "qboDocNumber", label: "QBO Doc #", sortable: false, minWidth: 140 },
    { id: "exported_at", label: "Exported At", sortable: true, minWidth: 180 },
    { id: "exportMethod", label: "Export Method", sortable: false, minWidth: 140 },
    { id: "exportBatchId", label: "Export Batch", sortable: false, minWidth: 220 },
    { id: "exportedById", label: "Exported By", sortable: false, minWidth: 120 },
    { id: "createdAt", label: "Created", sortable: true, minWidth: 180 },
    {
      id: "actions",
      label: "Actions",
      sortable: false,
      minWidth: 340,
      locked: true,
    },
  ];

  const renderCell = (colId, row) => {
    const base = process.env.FRONTEND_APP_URL || window.location.origin;
    switch (colId) {
      case "id":
        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.82rem",
              color: "primary.main",
              fontWeight: 700,
            }}
          >
            #{row.billId || row.id}
          </Typography>
        );
      case "poNumber":
        return (
          <a
            href={`${base}/instance/${instanceUniqueId}/sales/orders/${row.orderId}/purchase-order-home`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: "none",
              color: theme.palette.primary.main,
              fontWeight: 600,
            }}
          >
            {row.poNumber || "—"}
          </a>
        );
      case "orderId":
        return (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: C.textPri }}
          >
            {row.orderId || "—"}
          </Typography>
        );
      case "supplier": {
        const name =
          row.billPartyName ||
          row.billSupplierInfo?.Name ||
          row.billSupplierInfo?.businessName ||
          row.billSupplierInfo?.name ||
          row.billDecoratorInfo?.title ||
          row.billDecoratorInfo?.Description ||
          row.billSupplier?.Name ||
          row.billSupplier?.name ||
          getBillingSupplierName(row);

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: C.textPri, fontWeight: 500 }}
            >
              {name || "—"}
            </Typography>
            <Tooltip title="Manage QBO Vendor link">
              <span>
                <IconButton
                  size="small"
                  onClick={() => openLinkForBill(row)}
                  sx={{ color: C.textMuted }}
                >
                  <LinkIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      }
      case "allBillsReceived":
        return (
          <UIStatusChip
            label={row.allBillsReceived ? "Yes" : "No"}
            tone={row.allBillsReceived ? "success" : "neutral"}
          />
        );
      case "poAmount":
        return (
          <Typography
            variant="body2"
            sx={{ color: C.textPri, fontWeight: 600, fontFamily: "monospace" }}
          >
            {fmtMoney(row.poAmount, row.currency)}
          </Typography>
        );
      case "invoice":
        {
          const display = getBillInvoiceNumberDisplay(row);
          const href = getBillHref(row);
          if (href) {
            return (
              <a
                href={href}
                style={{ textDecoration: "none" }}
                aria-label={`Open bill ${row.billId} for invoice ${display.label}`}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    fontFamily: "monospace",
                    fontWeight: 700,
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                  }}
                >
                  {display.label}
                </Typography>
              </a>
            );
          }
          const content = (
          <Typography variant="body2" sx={{ color: C.textPri, fontFamily: "monospace" }}>
              {display.label}
          </Typography>
          );
          return display.notApplicable
            ? <Tooltip title="Invoice number not required for internal decoration">{content}</Tooltip>
            : content;
        }
      case "billTotal":
        return (
          <Typography
            variant="body2"
            sx={{ color: C.textPri, fontWeight: 600, fontFamily: "monospace" }}
          >
            {fmtMoney(row.billTotal, row.currency)}
          </Typography>
        );
      case "invoiceDate":
        return (
          <Typography variant="body2" sx={{ color: C.textSec }}>
            {row.invoiceDate
              ? new Date(row.invoiceDate).toLocaleDateString()
              : "—"}
          </Typography>
        );
      case "qboExportStatus": {
        const s = String(row.qboExportStatus || "").toLowerCase();
        const tone =
          s === "exported" ? "success" : s === "failed" ? "error" : "neutral";
        const label =
          s === "exported" ? "Exported" : s === "failed" ? "Failed" : "Pending";
        return <UIStatusChip label={label} tone={tone} />;
      }
      case "qboDocNumber":
        return (
          <Typography
            variant="body2"
            sx={{ color: C.textSec, fontFamily: "monospace" }}
          >
            {row.qboDocNumber || "—"}
          </Typography>
        );
      case "exported_at": {
        const d = row.exported_at || row.qboLastSyncAt;
        return (
          <Typography variant="body2" sx={{ color: C.textSec }}>
            {d ? new Date(d).toLocaleString() : "—"}
          </Typography>
        );
      }
      case "createdAt":
        return (
          <Typography variant="body2" sx={{ color: C.textSec }}>
            {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
          </Typography>
        );
      case "actions": {
        const alreadyExported = row.qboExportStatus === "exported";
        const canExport =
          qboExportEnabled && qbo.connected && !alreadyExported && !!row.billId;
        const label = !qboExportEnabled
          ? "Exports disabled"
          : !qbo.connected
          ? "Connect QBO"
          : alreadyExported
          ? "Exported"
          : "Export to QBO";
        const showLinkBtn =
          row.qboSyncError &&
          /VENDOR\s*UNMAPPED|SUPPLIER(?:\s+IS)?\s+NOT\s*LINKED|VendorRef\s*missing/i.test(
            String(row.qboSyncError),
          );
        return (
          <UITableActionCell align="flex-start">
            <Button
              size="small"
              variant="contained"
              disabled={!canExport}
              onClick={() => exportOne(row)}
              startIcon={
                exportingId === row.id ? null : (
                  <img src={QB_ICON} alt="" style={{ width: 14, height: 14 }} />
                )
              }
              sx={{
                fontWeight: 700,
                textTransform: "none",
                height: 28,
                px: 1.5,
                fontSize: "0.75rem",
                borderRadius: "8px",
                boxShadow: "none",
                ...(canExport
                  ? {
                      backgroundColor: "#2ca01c",
                      "&:hover": {
                        backgroundColor: "#279018",
                        boxShadow: "none",
                      },
                    }
                  : {}),
              }}
            >
              {exportingId === row.id ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                label
              )}
            </Button>
            {showLinkBtn && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => openLinkForBill(row)}
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  height: 28,
                  px: 1.5,
                  fontSize: "0.75rem",
                  borderRadius: "8px",
                }}
              >
                Link Vendor
              </Button>
            )}
            {row.qboSyncError && (
              <Tooltip title={row.qboSyncError}>
                <span>
                  <UIStatusChip label="Error" tone="error" />
                </span>
              </Tooltip>
            )}
          </UITableActionCell>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="instance-dashboard instance-orders-page">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box
          sx={{
            p: { xs: 1, md: 2 },
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 60px)",
            overflow: "hidden",
          }}
        >
          <UITableShell
            customLayout
            paperSx={{
              flex: 1,
              borderRadius: "10px",
              border: "1px solid",
              borderColor: "divider",
              borderTop: "3px solid",
              borderTopColor: "primary.main",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              boxShadow:
                "0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)",
            }}
          >
            <UITableToolbar sx={{ backgroundColor: C.paperBg, flexShrink: 0 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: C.textPri, lineHeight: 1 }}
                  >
                    Billing
                  </Typography>

                  <Chip
                    label={`${summary.count} total`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-neutral-bg)",
                      color: "var(--ec-status-neutral-text)",
                      border: "1px solid var(--ec-status-neutral-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 20, alignSelf: "center" }}
                  />

                  <Stack direction="row" spacing={0.5}>
                    {[VIEW.BILLS, VIEW.EXPORTED].map((v) => (
                      <Chip
                        key={v}
                        label={v === VIEW.BILLS ? "Bills" : "Exported Bills"}
                        size="small"
                        onClick={() => setView(v)}
                        sx={{
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 26,
                          backgroundColor:
                            view === v
                              ? "var(--ec-color-primary, #1976d2)"
                              : "var(--ec-status-neutral-bg)",
                          color:
                            view === v
                              ? "#fff"
                              : "var(--ec-status-neutral-text)",
                          border:
                            view === v
                              ? "1px solid transparent"
                              : "1px solid var(--ec-status-neutral-border)",
                          transition: "all 0.15s ease",
                          "& .MuiChip-label": { px: "10px" },
                        }}
                      />
                    ))}
                  </Stack>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 20, alignSelf: "center" }}
                  />

                  {/* QBO Connection toggle */}
                  <Chip
                    label="QBO Connection"
                    size="small"
                    onClick={() =>
                      setActiveTab(
                        activeTab === "connection" ? "search" : "connection",
                      )
                    }
                    sx={{
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 26,
                      backgroundColor:
                        activeTab === "connection"
                          ? "var(--ec-status-info-bg)"
                          : "var(--ec-status-neutral-bg)",
                      color:
                        activeTab === "connection"
                          ? "var(--ec-status-info-text)"
                          : "var(--ec-status-neutral-text)",
                      border: `1px solid ${
                        activeTab === "connection"
                          ? "var(--ec-status-info-border)"
                          : "var(--ec-status-neutral-border)"
                      }`,
                      "& .MuiChip-label": { px: "10px" },
                    }}
                  />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Show / hide columns">
                    <IconButton
                      size="small"
                      onClick={(e) => setColMenuEl(e.currentTarget)}
                      sx={{ color: "text.secondary" }}
                    >
                      <ViewColumnIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={colMenuEl}
                    open={Boolean(colMenuEl)}
                    onClose={() => setColMenuEl(null)}
                    slotProps={{
                      paper: { sx: { minWidth: 210, borderRadius: 2 } },
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        px: 2,
                        pt: 1.25,
                        pb: 0.5,
                        display: "block",
                        color: "text.disabled",
                      }}
                    >
                      Columns
                    </Typography>
                    <Divider sx={{ mb: 0.5 }} />
                    {ALL_BILLING_COLUMNS.filter((c) => !c.locked).map((col) => (
                      <MenuItem
                        key={col.id}
                        onClick={() => toggleCol(col.id)}
                        sx={{ py: 0.4 }}
                      >
                        <Checkbox
                          size="small"
                          checked={visibleCols.includes(col.id)}
                          sx={{
                            p: 0.5,
                            mr: 0.5,
                            color: "primary.main",
                            "&.Mui-checked": { color: "primary.main" },
                          }}
                        />
                        <ListItemText
                          primaryTypographyProps={{ fontSize: "0.875rem" }}
                        >
                          {col.label}
                        </ListItemText>
                      </MenuItem>
                    ))}
                    <Divider sx={{ mt: 0.5 }} />
                    <Box sx={{ display: "flex", gap: 1, px: 2, py: 1 }}>
                      <Button
                        size="small"
                        onClick={selectAllCols}
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                      >
                        Select all
                      </Button>
                      <Button
                        size="small"
                        onClick={clearAllCols}
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Menu>

                  {/* Density */}
                  <Tooltip title="Row density">
                    <IconButton
                      size="small"
                      onClick={(e) => setDensityMenuEl(e.currentTarget)}
                      sx={{ color: "text.secondary" }}
                    >
                      <SettingsIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={densityMenuEl}
                    open={Boolean(densityMenuEl)}
                    onClose={() => setDensityMenuEl(null)}
                    slotProps={{
                      paper: { sx: { minWidth: 180, borderRadius: 2 } },
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        px: 2,
                        pt: 1.25,
                        pb: 0.5,
                        display: "block",
                        color: "text.disabled",
                      }}
                    >
                      Row density
                    </Typography>
                    {["compact", "comfortable", "spacious"].map((d) => (
                      <MenuItem
                        key={d}
                        onClick={() => {
                          setDensity(d);
                          setDensityMenuEl(null);
                        }}
                        sx={{ py: 0.75 }}
                      >
                        <Checkbox
                          size="small"
                          checked={density === d}
                          sx={{
                            p: 0.5,
                            mr: 0.5,
                            color: "primary.main",
                            "&.Mui-checked": { color: "primary.main" },
                          }}
                        />
                        <ListItemText
                          primaryTypographyProps={{
                            fontSize: "0.875rem",
                            textTransform: "capitalize",
                          }}
                        >
                          {d}
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>

                  {/* Refresh */}
                  <Tooltip title="Refresh">
                    <IconButton
                      size="small"
                      onClick={fetchReport}
                      disabled={loading}
                      sx={{
                        color: "text.secondary",

                        width: 34,
                        height: 34,
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 0.5, height: 24, alignSelf: "center" }}
                  />

                  {/* Primary action button */}
                  {view === VIEW.BILLS ? (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={
                          !qboExportEnabled ||
                          !qbo.connected ||
                          selected.length === 0
                        }
                        onClick={exportSelected}
                        startIcon={
                          <img
                            src={QB_ICON}
                            alt=""
                            style={{ width: 14, height: 14 }}
                          />
                        }
                        sx={{
                          fontWeight: 700,
                          textTransform: "none",
                          height: 34,
                          px: 2,
                          boxShadow: "none",
                          backgroundColor: "#2ca01c",
                          "&:hover": {
                            backgroundColor: "#279018",
                            boxShadow: "none",
                          },
                          "&:disabled": { opacity: 0.5 },
                        }}
                      >
                        Export Selected ({selected.length})
                      </Button>
                      <Tooltip title="Generates a file and marks selected bills as Exported.">
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={selected.length === 0}
                            onClick={(e) => setFileExportMenuEl(e.currentTarget)}
                            sx={{ textTransform: "none", height: 34 }}
                          >
                            Export File
                          </Button>
                        </span>
                      </Tooltip>
                      <Menu
                        anchorEl={fileExportMenuEl}
                        open={Boolean(fileExportMenuEl)}
                        onClose={() => setFileExportMenuEl(null)}
                      >
                        <MenuItem onClick={() => { setFileExportMenuEl(null); runBillingFileExport("csv"); }}>
                          Export Selected CSV
                        </MenuItem>
                        <MenuItem onClick={() => { setFileExportMenuEl(null); runBillingFileExport("xlsx"); }}>
                          Export Selected XLSX
                        </MenuItem>
                      </Menu>
                      <Tooltip title="Downloads the current table only. Does not change export status.">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => setReportMenuEl(e.currentTarget)}
                          sx={{ textTransform: "none", height: 34 }}
                        >
                          Download Report
                        </Button>
                      </Tooltip>
                      <Menu
                        anchorEl={reportMenuEl}
                        open={Boolean(reportMenuEl)}
                        onClose={() => setReportMenuEl(null)}
                      >
                        <MenuItem onClick={() => { setReportMenuEl(null); runBillingReportDownload("csv"); }}>
                          Download Visible CSV
                        </MenuItem>
                        <MenuItem onClick={() => { setReportMenuEl(null); runBillingReportDownload("xlsx"); }}>
                          Download Visible XLSX
                        </MenuItem>
                      </Menu>
                      {selected.length > 0 && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setSelected([])}
                          sx={{
                            fontWeight: 600,
                            textTransform: "none",
                            height: 34,
                            color: "text.secondary",
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
                      onClick={async () => {
                        const accessToken = localStorage.getItem("accessToken");
                        const params = new URLSearchParams({
                          instanceUniqueId,
                        });
                        if (dateFrom) params.append("dateFrom", dateFrom);
                        if (dateTo) params.append("dateTo", dateTo);
                        if (billType === BILL_TYPES.INTERNAL_DECORATION) params.append("internalDecorationFilter", "internal_only");
                        params.append("ids", rows.map((row) => row.billId).filter(Boolean).join(","));
                        try {
                          const res = await fetch(
                            `${SERVER}/bill/exported/csv?${params}`,
                            { headers: { accessToken } },
                          );
                          if (!res.ok)
                            return alert(`Failed: ${await res.text()}`);
                          const a = document.createElement("a");
                          a.href = URL.createObjectURL(await res.blob());
                          a.download = `exported-bills-${new Date()
                            .toISOString()
                            .slice(0, 19)
                            .replace(/[:T]/g, "")}.csv`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                        } catch {
                          alert("Export failed.");
                        }
                      }}
                      sx={{
                        fontWeight: 700,
                        textTransform: "none",
                        height: 34,
                        px: 2,
                        boxShadow: "none",
                        backgroundColor: "#00897b",
                        "&:hover": {
                          backgroundColor: "#00766a",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Download CSV
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => runBillingReportDownload("xlsx")}
                      sx={{ fontWeight: 700, textTransform: "none", height: 34, px: 2 }}
                    >
                      Download XLSX
                    </Button>
                    <Tooltip title="Returns bills to the export queue. Does not undo external accounting entries.">
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={selected.length === 0}
                          onClick={bulkUnexportBillingSelected}
                          sx={{ fontWeight: 700, textTransform: "none", height: 34, px: 2 }}
                        >
                          Bulk Unexport Selected
                        </Button>
                      </span>
                    </Tooltip>
                    </>
                  )}
                </Stack>
              </Stack>

              {/* QBO Connection panel collapsible */}
              {activeTab === "connection" && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: qbo.connected ? "#e8f5e9" : "#fff8e1",
                    border: "1px solid",
                    borderColor: qbo.connected ? "#c8e6c9" : "#ffe0b2",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <img
                        src={QB_ICON}
                        alt="QuickBooks"
                        style={{ width: 28, height: 28, borderRadius: 6 }}
                      />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          QuickBooks Online
                        </Typography>
                        <Typography variant="body2" sx={{ color: C.textSec }}>
                          {qbo.checking
                            ? "Checking…"
                            : qbo.connected
                            ? `Connected${
                                qbo.realmId ? ` (Realm ${qbo.realmId})` : ""
                              }`
                            : "Not connected"}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={fetchQboStatus}
                        disabled={qbo.checking}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          height: 32,
                        }}
                      >
                        Refresh
                      </Button>
                      {!qbo.connected ? (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleQboConnect}
                          startIcon={
                            <img
                              src={QB_ICON}
                              alt=""
                              style={{ width: 14, height: 14 }}
                            />
                          }
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            height: 32,
                            boxShadow: "none",
                          }}
                        >
                          Connect to QuickBooks
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          disabled
                          startIcon={
                            <img
                              src={QB_ICON}
                              alt=""
                              style={{ width: 14, height: 14 }}
                            />
                          }
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            height: 32,
                            backgroundColor: "#2ca01c",
                            boxShadow: "none",
                          }}
                        >
                          Connected
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setQboLinkCtx({
                            partyType: "supplier",
                            partyId: null,
                            partyName: "",
                            supplierId: null,
                            supplierName: "",
                            billId: null,
                          });
                          setQboLinkOpen(true);
                        }}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          height: 32,
                        }}
                      >
                        Link Supplier → Vendor
                      </Button>

                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          <img
                            src={QB_ICON}
                            alt=""
                            style={{ width: 14, height: 14 }}
                          />
                        }
                        onClick={() => setQboCustomerOpen(true)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          height: 32,
                          boxShadow: "none",
                        }}
                      >
                        Create QBO Customer
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              )}

              <Divider sx={{ mt: 1.75, mb: 0 }} />

              {/* Filter row */}
              <Stack
                direction="row"
                spacing={1.5}
                mt={1.75}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
              >
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 170,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--ec-input-bg, #f8fafc)",
                    },
                  }}
                >
                  <InputLabel id="billing-view-label">Show</InputLabel>
                  <Select
                    labelId="billing-view-label"
                    value={view}
                    label="Show"
                    onChange={(e) => setView(e.target.value)}
                  >
                    <MenuItem value={VIEW.BILLS}>Bills</MenuItem>
                    <MenuItem value={VIEW.EXPORTED}>Exported Bills</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  placeholder="Search PO #…"
                  size="small"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchReport()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: C.textMuted }} />
                      </InputAdornment>
                    ),
                    endAdornment: poNumber ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => setPoNumber("")}
                        >
                          <ClearIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    flex: 2,
                    minWidth: 200,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--ec-input-bg, #f8fafc)",
                    },
                  }}
                />

                <TextField
                  size="small"
                  type="date"
                  label={
                    view === VIEW.EXPORTED
                      ? "From Export Date"
                      : "From Bill Date"
                  }
                  InputLabelProps={{ shrink: true }}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--ec-input-bg, #f8fafc)",
                    },
                  }}
                />
                <TextField
                  size="small"
                  type="date"
                  label={
                    view === VIEW.EXPORTED ? "To Export Date" : "To Bill Date"
                  }
                  InputLabelProps={{ shrink: true }}
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--ec-input-bg, #f8fafc)",
                    },
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="internal-decoration-filter-label">Bill Type</InputLabel>
                  <Select
                    labelId="internal-decoration-filter-label"
                    label="Bill Type"
                    value={billType}
                    onChange={(event) => {
                      setBillType(event.target.value);
                      if (event.target.value !== BILL_TYPES.OTHER_SERVICE) setServiceProvider("");
                      setSelected([]);
                      setPage(0);
                    }}
                  >
                    <MenuItem value={BILL_TYPES.ALL}>All</MenuItem>
                    <MenuItem value={BILL_TYPES.SUPPLIER}>Suppliers</MenuItem>
                    <MenuItem value={BILL_TYPES.DECORATOR}>Decorators</MenuItem>
                    <MenuItem value={BILL_TYPES.SHIPPING_ACCOUNT}>Shipping Accounts</MenuItem>
                    <MenuItem value={BILL_TYPES.OTHER_SERVICE}>Other Services</MenuItem>
                    <MenuItem value={BILL_TYPES.INTERNAL_DECORATION}>Internal Decorations</MenuItem>
                  </Select>
                </FormControl>
                {billType === BILL_TYPES.OTHER_SERVICE && (
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="instance-provider-filter">Service Provider</InputLabel>
                    <Select labelId="instance-provider-filter" label="Service Provider" value={serviceProvider}
                      onChange={(event) => { setServiceProvider(event.target.value); setSelected([]); setPage(0); }}>
                      <MenuItem value="">All providers</MenuItem>
                      {serviceProviders.map((provider) => <MenuItem key={provider.id} value={provider.id}>{provider.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}

                <Button
                  variant="contained"
                  size="small"
                  onClick={fetchReport}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={14} sx={{ color: "#fff" }} />
                    ) : (
                      <SearchIcon sx={{ fontSize: 15 }} />
                    )
                  }
                  sx={{
                    height: 40,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2.5,
                    whiteSpace: "nowrap",
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                  }}
                >
                  Get Report
                </Button>

                {(poNumber || dateFrom || dateTo || billType !== BILL_TYPES.ALL || serviceProvider) && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      setPoNumber("");
                      setDateFrom("");
                      setDateTo("");
                      setBillType(BILL_TYPES.ALL);
                      setServiceProvider("");
                    }}
                    sx={{
                      height: 40,
                      textTransform: "none",
                      fontWeight: 600,
                      color: "text.secondary",
                      "&:hover": {
                        color: "error.main",
                        backgroundColor: "var(--ec-status-error-bg)",
                      },
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>

              {/* Summary */}
              <Typography
                variant="body2"
                sx={{ mt: 1.25, color: C.textMuted, fontSize: "0.8125rem" }}
              >
                <strong style={{ color: C.textSec }}>
                  {view === VIEW.BILLS ? "Bills" : "Exported Bills"}:
                </strong>{" "}
                {summary.count} row{summary.count !== 1 ? "s" : ""} · Total{" "}
                {fmtMoney(summary.total)}
                {selected.length > 0 && (
                  <span
                    style={{
                      marginLeft: 12,
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    }}
                  >
                    · {selected.length} selected
                  </span>
                )}
              </Typography>
            </UITableToolbar>

            <TableContainer
              sx={{
                flex: 1,
                overflow: "auto",
                minHeight: 0,
                "&::-webkit-scrollbar": { width: 6, height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "var(--ec-color-border-strong, #cbd5e1)",
                  borderRadius: 99,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{ minWidth: 800, width: "100%" }}
              >
                <TableHead>
                  <TableRow>
                    {(view === VIEW.BILLS || view === VIEW.EXPORTED) && (
                      <TableCell
                        padding="checkbox"
                        sx={{
                          backgroundColor:
                            "var(--ec-table-head-bg-strong, #1e293b) !important",
                          borderBottom: "none",
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                        }}
                      >
                        <Checkbox
                          size="small"
                          indeterminate={
                            selected.length > 0 && selected.length < rows.length
                          }
                          checked={
                            rows.length > 0 && selected.length === rows.length
                          }
                          onChange={(e) =>
                            setSelected(
                              e.target.checked ? rows.map((r) => r.id) : [],
                            )
                          }
                          sx={{
                            color: "rgba(255,255,255,0.5)",
                            "&.Mui-checked,&.MuiCheckbox-indeterminate": {
                              color: "#fff",
                            },
                          }}
                        />
                      </TableCell>
                    )}
                    {ALL_BILLING_COLUMNS.filter((c) =>
                      visibleCols.includes(c.id),
                    ).map((col) => (
                      <TableCell
                        key={col.id}
                        onClick={() => col.sortable && handleSort(col.id)}
                        sx={{
                          minWidth: col.minWidth,
                          px: 2,
                          py: 1.25,
                          backgroundColor:
                            "var(--ec-table-head-bg-strong, #1e293b) !important",
                          color:
                            sortBy === col.id
                              ? "#fff"
                              : "rgba(255,255,255,0.6)",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          borderBottom: "none",
                          cursor: col.sortable ? "pointer" : "default",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          transition: "all 0.15s",
                          "&:hover": col.sortable
                            ? {
                                backgroundColor:
                                  "var(--ec-table-head-bg-hover, #334155) !important",
                                color: "#fff",
                              }
                            : {},
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {col.label}
                          {col.sortable && (
                            <Box
                              sx={{
                                display: "inline-flex",
                                ml: 0.25,
                                opacity: sortBy === col.id ? 1 : 0.4,
                              }}
                            >
                              {sortBy === col.id ? (
                                sortDir === "asc" ? (
                                  <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                                ) : (
                                  <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                                )
                              ) : (
                                <UnfoldMoreIcon sx={{ fontSize: 13 }} />
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody
                  sx={{
                    "& .MuiTableCell-root": {
                      borderBottom: `1px solid ${C.border}`,
                    },
                    "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                      borderBottom: "none",
                    },
                  }}
                >
                  {/* Skeleton */}
                  {loading &&
                    [...Array(8)].map((_, i) => (
                      <TableRow key={i}>
                        {(view === VIEW.BILLS || view === VIEW.EXPORTED) && (
                          <TableCell padding="checkbox">
                            <Skeleton
                              variant="rectangular"
                              width={18}
                              height={18}
                              sx={{ borderRadius: 1 }}
                            />
                          </TableCell>
                        )}
                        {ALL_BILLING_COLUMNS.filter((c) =>
                          visibleCols.includes(c.id),
                        ).map((col) => (
                          <TableCell
                            key={col.id}
                            sx={{ px: 2, py: densitySettings[density].py }}
                          >
                            <Skeleton
                              variant="rectangular"
                              height={16}
                              sx={{ borderRadius: 1, opacity: 1 - i * 0.08 }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}

                  {/* Empty state */}
                  {/* {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleCols.length + (view === VIEW.BILLS ? 1 : 0)} align="center" sx={{ py: 10, borderBottom: "none" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <Box sx={{ width: 64, height: 64, borderRadius: "50%",
                          backgroundColor: "var(--ec-color-surface-muted, #f1f5f9)",
                          display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <FolderOffIcon sx={{ fontSize: 32, color: C.textMuted }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600} color="text.secondary">No bills found</Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 340, textAlign: "center" }}>
                          Adjust the date range or PO filter and click <strong>Get Report</strong>.
                        </Typography>
                        <Button variant="outlined" size="small" onClick={fetchReport}
                          sx={{ textTransform: "none", fontWeight: 600, mt: 1 }}>Reload</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )} */}

                  {!loading && rows.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={visibleCols.length + ((view === VIEW.BILLS || view === VIEW.EXPORTED) ? 1 : 0)}
                        align="center"
                        sx={{ py: 10, borderBottom: "none" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          {/* Icon circle */}
                          <Box
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: "50%",
                              backgroundColor: "var(--ec-color-surface-muted)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FolderOffIcon
                              sx={{
                                fontSize: 32,
                                color: "var(--ec-color-text-muted)",
                              }}
                            />
                          </Box>

                          {/* Text */}
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              color="text.secondary"
                              gutterBottom
                            >
                              No bills found
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.disabled"
                              sx={{ maxWidth: 340 }}
                            >
                              {poNumber || dateFrom || dateTo
                                ? "No bills match your current filters. Try adjusting the date range or PO number."
                                : "No bills loaded yet. Click Get Report to fetch billing data."}
                            </Typography>
                          </Box>

                          {/* Actions */}
                          <Stack direction="row" spacing={1.5} mt={0.5}>
                            {(poNumber || dateFrom || dateTo) && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                                onClick={() => {
                                  setPoNumber("");
                                  setDateFrom("");
                                  setDateTo("");
                                }}
                                sx={{ textTransform: "none", fontWeight: 600 }}
                              >
                                Clear Filters
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              startIcon={
                                <CircularProgress sx={{ fontSize: 14 }} />
                              }
                              onClick={fetchReport}
                              sx={{ textTransform: "none", fontWeight: 700 }}
                            >
                              Get Report
                            </Button>
                          </Stack>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Data rows */}
                  {!loading &&
                    pagedRows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        selected={selected.includes(row.id)}
                        sx={{
                          backgroundColor: C.paperBg,
                          cursor: "default",
                          transition: "background-color 0.1s",
                          "&:hover": { backgroundColor: C.subtleBg },
                          "&.Mui-selected": {
                            backgroundColor:
                              "var(--ec-status-info-bg, #e3f2fd)",
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor:
                              "var(--ec-status-info-bg, #e3f2fd)",
                          },
                        }}
                      >
                        {(view === VIEW.BILLS || view === VIEW.EXPORTED) && (
                          <TableCell padding="checkbox" sx={{ px: 1 }}>
                            <Checkbox
                              size="small"
                              checked={selected.includes(row.id)}
                              onChange={(e) =>
                                setSelected((prev) =>
                                  e.target.checked
                                    ? [...prev, row.id]
                                    : prev.filter((id) => id !== row.id),
                                )
                              }
                            />
                          </TableCell>
                        )}
                        {ALL_BILLING_COLUMNS.filter((c) =>
                          visibleCols.includes(c.id),
                        ).map((col) => (
                          <TableCell
                            key={col.id}
                            sx={{
                              px: 2,
                              py: densitySettings[density].py,
                              fontSize: densitySettings[density].fontSize,
                            }}
                          >
                            {renderCell(col.id, row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                px: 2,
                backgroundColor: C.paperBg,
                borderTop: `1px solid ${C.border}`,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 52,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: C.textMuted, fontSize: "0.8125rem", pl: 1 }}
              >
                {rows.length > 0
                  ? `Showing ${page * rowsPerPage + 1}–${Math.min(
                      (page + 1) * rowsPerPage,
                      rows.length,
                    )} of ${rows.length}`
                  : "No results"}
              </Typography>
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(+e.target.value);
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  border: 0,
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "0.8125rem",
                    color: `${C.textMuted} !important`,
                    mb: 0,
                  },
                }}
              />
            </Box>
          </UITableShell>
        </Box>
      </div>

      <QboVendorLinkDialog
        open={qboLinkOpen}
        onClose={() => setQboLinkOpen(false)}
        instanceUniqueId={instanceUniqueId}
        partyType={qboLinkCtx.partyType}
        partyId={qboLinkCtx.partyId}
        partyName={qboLinkCtx.partyName}
        sourceType={qboLinkCtx.sourceType}
        sourceId={qboLinkCtx.sourceId}
        accountingSourceId={qboLinkCtx.accountingSourceId}
        sourceExternalKey={qboLinkCtx.sourceExternalKey}
        supplierId={qboLinkCtx.supplierId}
        supplierName={qboLinkCtx.supplierName}
        supplierSource={qboLinkCtx.supplierSource}
        selectedSupplierOption={qboLinkCtx.selectedSupplierOption}
        billId={qboLinkCtx.billId}
        onLinked={async () => {
          if (qboLinkCtx.billId) {
            const row = rows.find((r) => r.billId === qboLinkCtx.billId);
            if (row) await exportOne(row);
          }
          await fetchReport({ view });
        }}
      />
      <QboVendorLinkDialog
        open={qboCustomerOpen}
        onClose={() => setQboCustomerOpen(false)}
        instanceUniqueId={instanceUniqueId}
        onCreated={(cust) => {
          alert(
            `Created QBO Customer: ${
              cust?.displayName || cust?.companyName || cust?.id || "OK"
            }`,
          );
          setQboCustomerOpen(false);
        }}
      />
    </div>
  );
}
